import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import { logger } from "@app/services/logger";
import * as autoTuneApi from "./services/autoTuneApi";
import type {
  AutoTuneStatus,
  Recommendation,
  FilterStats,
  RejectedAnalysis,
  HistoryItem,
} from "./services/autoTuneApi";
import { PerformanceAnalytics } from "./components/PerformanceAnalytics";

type SubTab = "pending" | "filters" | "rejected" | "history" | "settings" | "performance";

type NotificationType = "success" | "error" | "warning" | "info";

interface NotificationState {
  message: string;
  type: NotificationType;
}

interface ConfirmationState {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
}

const SUB_TABS: Array<{ id: SubTab; label: string }> = [
  { id: "pending", label: "Pending" },
  { id: "filters", label: "Filters" },
  { id: "rejected", label: "Rejected Analysis" },
  { id: "history", label: "History" },
  { id: "performance", label: "Performance" },
  { id: "settings", label: "Settings" },
];

const FILTER_CATEGORIES = [
  { value: "all", label: "All" },
  { value: "pipeline", label: "Pipeline" },
  { value: "live_gate", label: "Live Gate" },
  { value: "sniper", label: "Sniper" },
];

const CATEGORY_LABELS: Record<string, string> = {
  pipeline: "Pipeline / Queue",
  live_gate: "Live Gate",
  sniper: "Sniper",
};

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.8) return "#22c55e";
  if (confidence >= 0.6) return "#eab308";
  return "#ef4444";
}

function getPassRateColor(rate: number): string {
  if (rate >= 70) return "#22c55e";
  if (rate >= 50) return "#eab308";
  return "#ef4444";
}

function getStatusLabel(status: string): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "applied":
      return "Applied";
    case "rejected":
      return "Rejected";
    case "rolled_back":
      return "Rolled Back";
    default:
      return status;
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case "pending":
      return "#eab308";
    case "applied":
      return "#22c55e";
    case "rejected":
      return "#ef4444";
    case "rolled_back":
      return "#6b7280";
    default:
      return "#6b7280";
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  modeBadge: {
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "0.8rem",
    fontWeight: 500,
  },
  subtitle: {
    fontSize: "0.85rem",
    color: "rgba(203, 213, 225, 0.7)",
    marginTop: "4px",
  },
  cardsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  statCard: {
    padding: "16px",
    borderRadius: "8px",
    background: "rgba(30, 41, 59, 0.8)",
    border: "1px solid rgba(148, 163, 184, 0.15)",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    marginTop: "8px",
  },
  statLabel: {
    fontSize: "0.8rem",
    color: "rgba(203, 213, 225, 0.7)",
    fontWeight: 500,
  },
  subTabsNav: {
    display: "flex",
    gap: "4px",
    marginBottom: "24px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
    paddingBottom: "0",
    overflowX: "auto" as const,
  },
  subTabButton: {
    padding: "10px 20px",
    border: "none",
    background: "transparent",
    color: "rgba(203, 213, 225, 0.7)",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 500,
    borderBottom: "2px solid transparent",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap" as const,
  },
  subTabButtonActive: {
    color: "#fff",
    borderBottom: "2px solid #3b82f6",
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    marginBottom: "16px",
    color: "#f1f5f9",
  },
  sectionSubtitle: {
    fontSize: "0.85rem",
    color: "rgba(203, 213, 225, 0.7)",
    marginBottom: "16px",
  },
  recommendationCard: {
    padding: "16px",
    borderRadius: "8px",
    background: "rgba(30, 41, 59, 0.6)",
    border: "1px solid rgba(148, 163, 184, 0.12)",
    marginBottom: "12px",
  },
  recommendationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "12px",
    gap: "12px",
  },
  confidenceBar: {
    width: "100%",
    height: "8px",
    borderRadius: "4px",
    background: "rgba(148, 163, 184, 0.15)",
    overflow: "hidden",
    marginTop: "8px",
  },
  confidenceFill: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.5s ease",
  },
  filterCategoryBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "0.85rem",
  },
  th: {
    padding: "10px 12px",
    textAlign: "left" as const,
    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
    color: "rgba(203, 213, 225, 0.7)",
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap" as const,
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.08)",
    verticalAlign: "middle" as const,
  },
  progressBar: {
    width: "100%",
    height: "6px",
    borderRadius: "3px",
    background: "rgba(148, 163, 184, 0.15)",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: "3px",
    transition: "width 0.5s ease",
  },
  filterPill: {
    display: "inline-flex",
    padding: "3px 10px",
    borderRadius: "4px",
    fontSize: "0.75rem",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    transition: "all 0.15s ease",
  },
  emptyState: {
    padding: "40px 20px",
    textAlign: "center" as const,
    color: "rgba(203, 213, 225, 0.5)",
  },
  errorState: {
    padding: "24px",
    borderRadius: "8px",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    textAlign: "center" as const,
    marginBottom: "16px",
  },
  loadingOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    color: "rgba(203, 213, 225, 0.7)",
  },
  formGroup: {
    marginBottom: "16px",
  },
  formLabel: {
    display: "block",
    fontSize: "0.85rem",
    fontWeight: 500,
    color: "rgba(203, 213, 225, 0.8)",
    marginBottom: "6px",
  },
  formInput: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    background: "rgba(15, 23, 42, 0.6)",
    color: "#f1f5f9",
    fontSize: "0.85rem",
    outline: "none",
    boxSizing: "border-box" as const,
  },
  formSelect: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    background: "rgba(15, 23, 42, 0.6)",
    color: "#f1f5f9",
    fontSize: "0.85rem",
    outline: "none",
  },
  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    gap: "12px",
    flexWrap: "wrap" as const,
  },
  paginationInfo: {
    fontSize: "0.8rem",
    color: "rgba(203, 213, 225, 0.6)",
  },
  searchInput: {
    padding: "6px 12px",
    borderRadius: "6px",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    background: "rgba(15, 23, 42, 0.6)",
    color: "#f1f5f9",
    fontSize: "0.85rem",
    outline: "none",
    width: "200px",
  },
  settingsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },
  settingsCard: {
    padding: "20px",
    borderRadius: "8px",
    background: "rgba(30, 41, 59, 0.6)",
    border: "1px solid rgba(148, 163, 184, 0.12)",
  },
  settingsCardTitle: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#f1f5f9",
    marginBottom: "16px",
  },
  toggleTrack: {
    width: "44px",
    height: "24px",
    borderRadius: "12px",
    cursor: "pointer",
    position: "relative" as const,
    transition: "background 0.2s ease",
    border: "none",
    flexShrink: 0,
  },
  toggleThumb: {
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    background: "#fff",
    position: "absolute" as const,
    top: "3px",
    transition: "left 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
  },
  confirmationOverlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
  },
  confirmationDialog: {
    background: "rgba(30, 41, 59, 0.98)",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "420px",
    width: "90%",
    boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
  },
  notificationToast: {
    position: "fixed" as const,
    top: "20px",
    right: "20px",
    zIndex: 2000,
    padding: "12px 16px",
    borderRadius: "8px",
    minWidth: "280px",
    maxWidth: "400px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
    borderLeft: "4px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    animation: "slideInRight 0.25s ease-out",
  },
  filterBar: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
    flexWrap: "wrap" as const,
  },
};

function renderNotificationBanner(
  notification: NotificationState | null,
  onDismiss: () => void
): JSX.Element | null {
  if (!notification) return null;

  const typeColors: Record<NotificationType, { bg: string; border: string; color: string }> = {
    success: { bg: "rgba(34, 197, 94, 0.1)", border: "#22c55e", color: "#22c55e" },
    error: { bg: "rgba(239, 68, 68, 0.1)", border: "#ef4444", color: "#ef4444" },
    warning: { bg: "rgba(234, 179, 8, 0.1)", border: "#eab308", color: "#eab308" },
    info: { bg: "rgba(59, 130, 246, 0.1)", border: "#3b82f6", color: "#3b82f6" },
  };

  const tc = typeColors[notification.type];
  const icons: Record<NotificationType, string> = {
    success: "\u2713",
    error: "\u2715",
    warning: "\u26A0",
    info: "\u2139",
  };

  return (
    <div
      style={{
        ...styles.notificationToast,
        background: tc.bg,
        color: tc.color,
        borderLeftColor: tc.border,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
        <span style={{ fontSize: "1.1rem" }}>{icons[notification.type]}</span>
        <span style={{ fontSize: "0.85rem", lineHeight: "1.4", color: "#f1f5f9" }}>
          {notification.message}
        </span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          background: "transparent",
          border: "none",
          color: "rgba(203, 213, 225, 0.6)",
          cursor: "pointer",
          padding: "4px",
          fontSize: "1.2rem",
          lineHeight: "1",
        }}
        aria-label="Dismiss"
      >
        \u00D7
      </button>
    </div>
  );
}

function renderConfirmationDialog(
  state: ConfirmationState | null,
  onCancel: () => void,
  isProcessing: boolean
): JSX.Element | null {
  if (!state) return null;

  return (
    <div style={styles.confirmationOverlay} onClick={onCancel}>
      <div style={styles.confirmationDialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 8px", fontSize: "1.1rem", color: "#f1f5f9" }}>
          {state.title}
        </h3>
        <p style={{ margin: "0 0 20px", fontSize: "0.85rem", color: "rgba(203, 213, 225, 0.7)", lineHeight: "1.5" }}>
          {state.message}
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={state.onConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? "Processing..." : state.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SkeletonRow({ cols }: { cols: number }): JSX.Element {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} style={styles.td}>
          <Skeleton height={16} width={i === 0 ? "120px" : i === cols - 1 ? "80px" : "100%"} />
        </td>
      ))}
    </tr>
  );
}

function EmptyState({ message }: { message: string }): JSX.Element {
  return (
    <div style={styles.emptyState}>
      <div style={{ fontSize: "0.9rem", marginBottom: "8px" }}>{message}</div>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div style={styles.errorState}>
      <div style={{ fontSize: "0.85rem", color: "#fca5a5", marginBottom: "12px" }}>
        {message}
      </div>
      {onRetry && (
        <Button type="button" variant="ghost" size="small" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

function FilterCategoryBadge({ category }: { category: string }): JSX.Element {
  const colorMap: Record<string, string> = {
    pipeline: "#3b82f6",
    live_gate: "#a855f7",
    sniper: "#f59e0b",
  };
  const bgMap: Record<string, string> = {
    pipeline: "rgba(59, 130, 246, 0.15)",
    live_gate: "rgba(168, 85, 247, 0.15)",
    sniper: "rgba(245, 158, 11, 0.15)",
  };

  return (
    <span
      style={{
        ...styles.filterCategoryBadge,
        color: colorMap[category] || "#6b7280",
        background: bgMap[category] || "rgba(107, 114, 128, 0.15)",
      }}
    >
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

function PendingRecommendationsList({
  recommendations,
  loading,
  error,
  onApply,
  onReject,
  onRetry,
  processingIds,
}: {
  recommendations: Recommendation[];
  loading: boolean;
  error: string | null;
  onApply: (id: string) => void;
  onReject: (id: string) => void;
  onRetry?: () => void;
  processingIds: Set<string>;
}): JSX.Element {
  const categorized = useMemo(() => {
    const groups: Record<string, Recommendation[]> = {};
    for (const rec of recommendations) {
      const cat = rec.filterCategory || "other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(rec);
    }
    return groups;
  }, [recommendations]);

  if (loading && recommendations.length === 0) {
    return (
      <div>
        {[1, 2, 3].map((i) => (
          <div key={i} style={styles.recommendationCard}>
            <Skeleton height={20} width="60%" />
            <div style={{ marginTop: "12px" }}>
              <Skeleton height={8} />
            </div>
            <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
              <Skeleton height={32} width="80px" />
              <Skeleton height={32} width="80px" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (recommendations.length === 0) {
    return (
      <EmptyState message="No pending recommendations. Run an analysis to generate recommendations." />
    );
  }

  const categoryOrder = ["pipeline", "live_gate", "sniper"];

  return (
    <div>
      {categoryOrder.map((cat) => {
        const items = categorized[cat];
        if (!items || items.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: "24px" }}>
            <h4 style={styles.sectionTitle}>
              <FilterCategoryBadge category={cat} />
            </h4>
            {items.map((rec) => (
              <div key={rec.id} style={styles.recommendationCard}>
                <div style={styles.recommendationHeader}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                      <span style={{ fontWeight: 600, color: "#f1f5f9", fontSize: "0.95rem" }}>
                        {rec.settingLabel}
                      </span>
                      <FilterCategoryBadge category={rec.filterCategory} />
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "rgba(203, 213, 225, 0.6)", marginBottom: "8px" }}>
                      {rec.settingKey}
                    </div>
                    <div style={{ fontSize: "0.9rem", marginBottom: "8px" }}>
                      <span style={{ color: "rgba(203, 213, 225, 0.7)" }}>
                        {rec.currentValue}
                      </span>
                      <span style={{ color: "rgba(203, 213, 225, 0.4)", margin: "0 8px" }}>
                        \u2192
                      </span>
                      <span style={{ color: "#22c55e", fontWeight: 600 }}>
                        {rec.suggestedValue}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "rgba(203, 213, 225, 0.7)", marginBottom: "4px" }}>
                      {rec.reason}
                    </div>
                    {rec.sampleSize > 0 && (
                      <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.5)", marginBottom: "8px" }}>
                        Sample: {rec.sampleSize} trades
                        {rec.expectedWinRateAfter != null && (
                          <span>
                            {" "}
                            | Expected win rate: {(rec.expectedWinRateAfter).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", marginBottom: "4px" }}>
                    <span style={{ color: "rgba(203, 213, 225, 0.6)" }}>Confidence</span>
                    <span style={{ color: getConfidenceColor(rec.confidence), fontWeight: 600 }}>
                      {(rec.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div style={styles.confidenceBar}>
                    <div
                      style={{
                        ...styles.confidenceFill,
                        width: `${Math.min(rec.confidence * 100, 100)}%`,
                        background: getConfidenceColor(rec.confidence),
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    onClick={() => onApply(rec.id)}
                    disabled={processingIds.has(rec.id)}
                  >
                    {processingIds.has(rec.id) ? "Applying..." : "Apply"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="small"
                    onClick={() => onReject(rec.id)}
                    disabled={processingIds.has(rec.id)}
                    style={{ color: "#ef4444" }}
                  >
                    {processingIds.has(rec.id) ? "Rejecting..." : "Reject"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function FilterPerformanceGrid({
  filterStats,
  loading,
  error,
  onRetry,
}: {
  filterStats: FilterStats[];
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
}): JSX.Element {
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered = useMemo(() => {
    if (activeCategory === "all") return filterStats;
    return filterStats.filter((f) => f.filterCategory === activeCategory);
  }, [filterStats, activeCategory]);

  if (loading && filterStats.length === 0) {
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={styles.recommendationCard}>
              <Skeleton height={18} width="70%" />
              <div style={{ marginTop: "12px" }}>
                <Skeleton height={6} />
              </div>
              <div style={{ marginTop: "8px" }}>
                <Skeleton height={14} width="50%" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (filterStats.length === 0) {
    return (
      <EmptyState message="No filter performance data available. Tracking data will appear once filters are evaluated." />
    );
  }

  return (
    <div>
      <div style={styles.filterBar}>
        {FILTER_CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            type="button"
            onClick={() => setActiveCategory(cat.value)}
            style={{
              ...styles.filterPill,
              background:
                activeCategory === cat.value
                  ? "rgba(59, 130, 246, 0.2)"
                  : "rgba(148, 163, 184, 0.08)",
              color: activeCategory === cat.value ? "#60a5fa" : "rgba(203, 213, 225, 0.7)",
              borderColor:
                activeCategory === cat.value
                  ? "rgba(59, 130, 246, 0.4)"
                  : "rgba(148, 163, 184, 0.15)",
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState message={`No filters found for the selected category.`} />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "12px",
          }}
        >
          {filtered.map((f, idx) => (
            <div
              key={`${f.filterCategory}-${f.filterName}-${idx}`}
              style={styles.recommendationCard}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <div>
                  <div style={{ fontWeight: 600, color: "#f1f5f9", fontSize: "0.9rem" }}>
                    {f.filterName}
                  </div>
                  <FilterCategoryBadge category={f.filterCategory} />
                </div>
                <div
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    color: getPassRateColor(f.passRate),
                  }}
                >
                  {f.passRate.toFixed(0)}%
                </div>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${Math.min(f.passRate, 100)}%`,
                    background: getPassRateColor(f.passRate),
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                  fontSize: "0.75rem",
                  color: "rgba(203, 213, 225, 0.6)",
                }}
              >
                <span>Passed: {f.passed}</span>
                <span>Failed: {f.failed}</span>
                <span>Total: {f.total}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RejectedAnalysisPanel({
  analysis,
  loading,
  error,
  onRetry,
  onRunAnalysis,
  analysing,
}: {
  analysis: RejectedAnalysis | null;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  onRunAnalysis?: () => void;
  analysing?: boolean;
}): JSX.Element {
  if (loading && !analysis) {
    return (
      <div style={styles.cardsRow}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={styles.statCard}>
            <Skeleton height={14} width="60%" />
            <div style={{ marginTop: "12px" }}>
              <Skeleton height={28} width="40%" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (!analysis || analysis.total === 0) {
    return (
      <EmptyState message="No rejected trade analysis available. Run a rejected analysis to generate data." />
    );
  }

  const classificationData = [
    { label: "False Positive", value: analysis.falsePositive, color: "#ef4444" },
    { label: "Correct Reject", value: analysis.correctReject, color: "#22c55e" },
    { label: "Partial Miss", value: analysis.partialMiss, color: "#eab308" },
    { label: "Inconclusive", value: analysis.inconclusive, color: "#6b7280" },
  ];

  const maxVal = Math.max(...classificationData.map((d) => d.value), 1);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={styles.sectionTitle}>Classification Breakdown</div>
        {onRunAnalysis && (
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={onRunAnalysis}
            disabled={analysing}
          >
            {analysing ? "Analysing..." : "Run Rejected Analysis"}
          </Button>
        )}
      </div>
      <div style={styles.cardsRow}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Rejected</div>
          <div style={styles.statValue}>{analysis.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>False Positive</div>
          <div
            style={{
              ...styles.statValue,
              color: analysis.falsePositiveRate > 40 ? "#ef4444" : analysis.falsePositiveRate > 25 ? "#eab308" : "#22c55e",
            }}
          >
            {analysis.falsePositive}
            <span style={{ fontSize: "0.8rem", marginLeft: "4px", fontWeight: 400 }}>
              ({(analysis.falsePositiveRate).toFixed(1)}%)
            </span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Correct Reject</div>
          <div style={{ ...styles.statValue, color: "#22c55e" }}>{analysis.correctReject}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Missed Profit</div>
          <div style={{ ...styles.statValue, color: "#f97316" }}>
            ${analysis.totalMissedProfitUsdt.toFixed(2)}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "24px" }}>
        {classificationData.map((item) => {
          const pct = item.value / maxVal;
          return (
            <div key={item.label}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.85rem",
                  marginBottom: "4px",
                }}
              >
                <span style={{ color: item.color }}>{item.label}</span>
                <span style={{ color: "rgba(203, 213, 225, 0.7)" }}>
                  {item.value} ({analysis.total > 0 ? ((item.value / analysis.total) * 100).toFixed(1) : 0}%)
                </span>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${Math.min(pct * 100, 100)}%`,
                    background: item.color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          ...styles.statCard,
          background: analysis.falsePositiveRate > 40
            ? "rgba(239, 68, 68, 0.08)"
            : "rgba(34, 197, 94, 0.08)",
          borderColor: analysis.falsePositiveRate > 40
            ? "rgba(239, 68, 68, 0.3)"
            : "rgba(34, 197, 94, 0.3)",
        }}
      >
        <div style={styles.statLabel}>Assessment</div>
        <div style={{ fontSize: "0.9rem", marginTop: "8px", lineHeight: "1.5", color: "rgba(203, 213, 225, 0.8)" }}>
          {analysis.falsePositiveRate > 40
            ? `High false positive rate (${(analysis.falsePositiveRate).toFixed(1)}%). Filters may be too aggressive, causing missed profitable trades. Consider reviewing gate filter thresholds.`
            : analysis.falsePositiveRate > 25
            ? `Moderate false positive rate (${(analysis.falsePositiveRate).toFixed(1)}%). Filter performance is acceptable but room for improvement exists.`
            : `Low false positive rate (${(analysis.falsePositiveRate).toFixed(1)}%). Filters are performing well with minimal missed profits.`}
        </div>
      </div>
    </div>
  );
}

function HistoryTable({
  items,
  loading,
  error,
  onRetry,
  onRollback,
  processingIds,
}: {
  items: HistoryItem[];
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  onRollback: (id: string) => void;
  processingIds: Set<string>;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 15;

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) =>
        item.settingLabel.toLowerCase().includes(q) ||
        item.settingKey.toLowerCase().includes(q) ||
        item.filterCategory.toLowerCase().includes(q) ||
        getStatusLabel(item.status).toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const paginated = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  useEffect(() => {
    setPage(0);
  }, [search]);

  if (loading && items.length === 0) {
    return (
      <div>
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Date", "Setting", "Change", "Category", "Confidence", "Status", "Applied By", ""].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonRow key={i} cols={8} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (items.length === 0) {
    return (
      <EmptyState message="No history available. Changes will appear here once recommendations are applied or rejected." />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: "16px" }}>
        <input
          type="text"
          placeholder="Search by setting, category, or status..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {paginated.length === 0 ? (
        <EmptyState message="No history items match your search." />
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Setting</th>
                  <th style={styles.th}>Change</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Confidence</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Applied By</th>
                  <th style={styles.th} />
                </tr>
              </thead>
              <tbody>
                {paginated.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.td}>
                      <span style={{ fontSize: "0.8rem", color: "rgba(203, 213, 225, 0.7)" }}>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "\u2014"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 500, color: "#f1f5f9", fontSize: "0.85rem" }}>
                        {item.settingLabel}
                      </div>
                      <div style={{ fontSize: "0.7rem", color: "rgba(203, 213, 225, 0.5)", marginTop: "2px" }}>
                        {item.settingKey}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: "rgba(203, 213, 225, 0.7)", fontSize: "0.8rem" }}>
                        {item.currentValue}
                      </span>
                      <span style={{ color: "rgba(203, 213, 225, 0.4)", margin: "0 6px", fontSize: "0.8rem" }}>
                        \u2192
                      </span>
                      <span style={{ color: "#22c55e", fontWeight: 600, fontSize: "0.85rem" }}>
                        {item.suggestedValue}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <FilterCategoryBadge category={item.filterCategory} />
                      {item.filterName && (
                        <div style={{ fontSize: "0.7rem", color: "rgba(203, 213, 225, 0.5)", marginTop: "2px" }}>
                          {item.filterName}
                        </div>
                      )}
                    </td>
                    <td style={{ ...styles.td, fontSize: "0.8rem" }}>
                      {item.confidence != null
                        ? <span style={{ color: item.confidence >= 0.7 ? "#22c55e" : item.confidence >= 0.5 ? "#f97316" : "#ef4444", fontWeight: 600 }}>
                            {(item.confidence * 100).toFixed(0)}%
                          </span>
                        : "\u2014"}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          color: getStatusColor(item.status),
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      >
                        {getStatusLabel(item.status)}
                        {item.status === "rolled_back" && item.rollbackReason && (
                          <span style={{ color: "rgba(203, 213, 225, 0.5)", fontWeight: 400, fontSize: "0.7rem", marginLeft: "4px" }}>
                            ({item.rollbackReason})
                          </span>
                        )}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontSize: "0.8rem", color: "rgba(203, 213, 225, 0.7)" }}>
                      {item.appliedBy || "\u2014"}
                    </td>
                    <td style={styles.td}>
                      {item.status === "applied" && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          onClick={() => onRollback(item.id)}
                          disabled={processingIds.has(item.id)}
                          style={{ color: "#f97316" }}
                        >
                          {processingIds.has(item.id) ? "..." : "Rollback"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={styles.paginationRow}>
              <div style={styles.paginationInfo}>
                Showing {safePage * pageSize + 1}\u2013{Math.min((safePage + 1) * pageSize, filtered.length)} of{" "}
                {filtered.length}
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                >
                  Previous
                </Button>
                <span style={{ fontSize: "0.8rem", color: "rgba(203, 213, 225, 0.6)" }}>
                  Page {safePage + 1} of {totalPages}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={safePage >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SettingsPanel({
  settings,
  loading,
  error,
  onRetry,
  onSave,
  onModeChange,
  saving,
}: {
  settings: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  onSave: (updated: Record<string, unknown>) => void;
  onModeChange: (mode: string) => void;
  saving: boolean;
}): JSX.Element {
  const [localSettings, setLocalSettings] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings) {
      setLocalSettings({ ...settings });
      setDirty(false);
    }
  }, [settings]);

  const updateSetting = useCallback((key: string, value: unknown) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  if (loading && !settings) {
    return (
      <div style={styles.settingsGrid}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={styles.settingsCard}>
            <Skeleton height={18} width="50%" />
            <div style={{ marginTop: "16px" }}>
              <Skeleton height={36} />
            </div>
            <div style={{ marginTop: "12px" }}>
              <Skeleton height={36} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (!settings) {
    return <EmptyState message="Settings are not available." />;
  }

  const enabled = Boolean(localSettings["enabled"] ?? settings["enabled"] ?? false);
  const mode = String(localSettings["mode"] ?? settings["mode"] ?? "manual");
  const maxDailyChanges = Number(localSettings["maxDailyChanges"] ?? settings["maxDailyChanges"] ?? 5);
  const minConfidence = Number(localSettings["minConfidence"] ?? settings["minConfidence"] ?? 0.7);
  const maxChangePercent = Number(localSettings["maxChangePercent"] ?? settings["maxChangePercent"] ?? 20);
  const minSampleSize = Number(localSettings["minSampleSize"] ?? settings["minSampleSize"] ?? 30);
  const notificationsEnabled = Boolean(
    localSettings["notificationsEnabled"] ?? settings["notificationsEnabled"] ?? true
  );

  return (
    <div>
      <div style={styles.settingsGrid}>
        <div style={styles.settingsCard}>
          <div style={styles.settingsCardTitle}>Master Controls</div>
          <div style={styles.formGroup}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={styles.formLabel}>Enable Auto-Tune</div>
                <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.5)" }}>
                  Master toggle for the entire auto-tune system
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateSetting("enabled", !enabled)}
                style={{
                  ...styles.toggleTrack,
                  background: enabled ? "#22c55e" : "rgba(148, 163, 184, 0.3)",
                }}
                aria-label={enabled ? "Disable" : "Enable"}
              >
                <div
                  style={{
                    ...styles.toggleThumb,
                    left: enabled ? "23px" : "3px",
                  }}
                />
              </button>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Mode</label>
            <select
              value={mode}
              onChange={(e) => {
                updateSetting("mode", e.target.value);
                onModeChange(e.target.value);
              }}
              style={styles.formSelect}
            >
              <option value="disabled">Disabled</option>
              <option value="manual">Manual</option>
              <option value="semi_automatic">Semi-Automatic</option>
              <option value="auto">Automatic</option>
            </select>
            <div style={{ fontSize: "0.7rem", color: "rgba(203, 213, 225, 0.5)", marginTop: "4px" }}>
              Manual: recommendations require approval. Automatic: applied automatically within limits.
            </div>
          </div>
        </div>

        <div style={styles.settingsCard}>
          <div style={styles.settingsCardTitle}>Analysis</div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Analysis Interval (hours)</label>
            <input
              type="number"
              min={1}
              max={24}
              value={Number(localSettings["analysisIntervalHours"] ?? settings["analysisIntervalHours"] ?? 6)}
              onChange={(e) => updateSetting("analysisIntervalHours", Math.max(1, Math.min(24, parseInt(e.target.value) || 6)))}
              style={styles.formInput}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Rejected Analysis Lookback (hours)</label>
            <input
              type="number"
              min={4}
              max={168}
              value={Number(localSettings["rejectedAnalysisHours"] ?? settings["rejectedAnalysisHours"] ?? 24)}
              onChange={(e) =>
                updateSetting("rejectedAnalysisHours", Math.max(4, Math.min(168, parseInt(e.target.value) || 24)))
              }
              style={styles.formInput}
            />
          </div>
        </div>

        <div style={styles.settingsCard}>
          <div style={styles.settingsCardTitle}>Safety Limits</div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Min Sample Size</label>
            <input
              type="number"
              min={10}
              max={200}
              value={minSampleSize}
              onChange={(e) => updateSetting("minSampleSize", Math.max(10, Math.min(200, parseInt(e.target.value) || 30)))}
              style={styles.formInput}
            />
            <div style={{ fontSize: "0.7rem", color: "rgba(203, 213, 225, 0.5)", marginTop: "4px" }}>
              Minimum trades required before generating a recommendation
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Min Confidence</label>
            <input
              type="number"
              min={0.5}
              max={0.95}
              step={0.05}
              value={minConfidence}
              onChange={(e) => updateSetting("minConfidence", Math.max(0.5, Math.min(0.95, parseFloat(e.target.value) || 0.7)))}
              style={styles.formInput}
            />
            <div style={{ fontSize: "0.7rem", color: "rgba(203, 213, 225, 0.5)", marginTop: "4px" }}>
              Minimum confidence threshold for recommendations
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Max Changes Per Day</label>
            <input
              type="number"
              min={1}
              max={20}
              value={maxDailyChanges}
              onChange={(e) => updateSetting("maxDailyChanges", Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
              style={styles.formInput}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Max Change % Per Recommendation</label>
            <input
              type="number"
              min={5}
              max={50}
              value={maxChangePercent}
              onChange={(e) => updateSetting("maxChangePercent", Math.max(5, Math.min(50, parseInt(e.target.value) || 20)))}
              style={styles.formInput}
            />
          </div>
        </div>

        <div style={styles.settingsCard}>
          <div style={styles.settingsCardTitle}>Notifications</div>
          <div style={styles.formGroup}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={styles.formLabel}>Notifications Enabled</div>
                <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.5)" }}>
                  Receive notifications for new recommendations
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateSetting("notificationsEnabled", !notificationsEnabled)}
                style={{
                  ...styles.toggleTrack,
                  background: notificationsEnabled ? "#22c55e" : "rgba(148, 163, 184, 0.3)",
                }}
                aria-label={notificationsEnabled ? "Disable" : "Enable"}
              >
                <div
                  style={{
                    ...styles.toggleThumb,
                    left: notificationsEnabled ? "23px" : "3px",
                  }}
                />
              </button>
            </div>
          </div>
          <div style={styles.formGroup}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={styles.formLabel}>Telegram Notifications</div>
                <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.5)" }}>
                  Send critical changes via Telegram
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  updateSetting(
                    "telegramNotifications",
                    !(localSettings["telegramNotifications"] ?? settings["telegramNotifications"] ?? false)
                  )
                }
                style={{
                  ...styles.toggleTrack,
                  background: (localSettings["telegramNotifications"] ?? settings["telegramNotifications"] ?? false)
                    ? "#22c55e"
                    : "rgba(148, 163, 184, 0.3)",
                }}
                aria-label="Toggle Telegram notifications"
              >
                <div
                  style={{
                    ...styles.toggleThumb,
                    left: (localSettings["telegramNotifications"] ?? settings["telegramNotifications"] ?? false)
                      ? "23px"
                      : "3px",
                  }}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
        <Button
          type="button"
          variant="primary"
          onClick={() => onSave(localSettings)}
          disabled={!dirty || saving}
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
        {dirty && (
          <Button type="button" variant="ghost" onClick={() => setDirty(false)} disabled={saving}>
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}

export function AutoTuneTab(): JSX.Element {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("pending");
  const [status, setStatus] = useState<AutoTuneStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [pendingRecs, setPendingRecs] = useState<Recommendation[]>([]);
  const [pendingRecsLoading, setPendingRecsLoading] = useState(false);
  const [pendingRecsError, setPendingRecsError] = useState<string | null>(null);

  const [filterStats, setFilterStats] = useState<FilterStats[]>([]);
  const [filterStatsLoading, setFilterStatsLoading] = useState(false);
  const [filterStatsError, setFilterStatsError] = useState<string | null>(null);

  const [rejectedAnalysis, setRejectedAnalysis] = useState<RejectedAnalysis | null>(null);
  const [rejectedAnalysisLoading, setRejectedAnalysisLoading] = useState(false);
  const [rejectedAnalysisError, setRejectedAnalysisError] = useState<string | null>(null);

  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(null);
  const [processingConfirmation, setProcessingConfirmation] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  const showNotification = useCallback((message: string, type: NotificationType) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const data = await autoTuneApi.getStatus();
      setStatus(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load status";
      setStatusError(msg);
      logger.error("[AutoTuneTab] loadStatus:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadPendingRecs = useCallback(async () => {
    setPendingRecsLoading(true);
    setPendingRecsError(null);
    try {
      const data = await autoTuneApi.getRecommendations(undefined, "pending");
      setPendingRecs(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load pending recommendations";
      setPendingRecsError(msg);
      logger.error("[AutoTuneTab] loadPendingRecs:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setPendingRecsLoading(false);
    }
  }, []);

  const loadFilterStats = useCallback(async () => {
    setFilterStatsLoading(true);
    setFilterStatsError(null);
    try {
      const data = await autoTuneApi.getFilterStats();
      if (Array.isArray(data)) {
        setFilterStats(data);
      } else {
        setFilterStats(data ? [data] : []);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load filter stats";
      setFilterStatsError(msg);
      logger.error("[AutoTuneTab] loadFilterStats:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setFilterStatsLoading(false);
    }
  }, []);

  const loadRejectedAnalysis = useCallback(async () => {
    setRejectedAnalysisLoading(true);
    setRejectedAnalysisError(null);
    try {
      const data = await autoTuneApi.getRejectedAnalysis();
      setRejectedAnalysis(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load rejected analysis";
      setRejectedAnalysisError(msg);
      logger.error("[AutoTuneTab] loadRejectedAnalysis:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setRejectedAnalysisLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await autoTuneApi.getHistory();
      setHistoryItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load history";
      setHistoryError(msg);
      logger.error("[AutoTuneTab] loadHistory:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const data = await autoTuneApi.getSettings();
      setSettings(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load settings";
      setSettingsError(msg);
      logger.error("[AutoTuneTab] loadSettings:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadPendingRecs();
    loadFilterStats();
    loadRejectedAnalysis();
    loadHistory();
    loadSettings();
  }, [loadStatus, loadPendingRecs, loadFilterStats, loadRejectedAnalysis, loadHistory, loadSettings]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadStatus();
      if (activeSubTab === "pending") loadPendingRecs();
      if (activeSubTab === "filters") loadFilterStats();
      if (activeSubTab === "rejected") loadRejectedAnalysis();
      if (activeSubTab === "history") loadHistory();
      if (activeSubTab === "settings") loadSettings();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeSubTab, loadStatus, loadPendingRecs, loadFilterStats, loadRejectedAnalysis, loadHistory, loadSettings]);

  const handleApply = useCallback(async (id: string) => {
    const rec = pendingRecs.find((r) => r.id === id);
    if (!rec) return;
    const reason = rec.reason || rec.settingLabel;
    setConfirmation({
      title: "Apply Recommendation",
      message: `Apply "${reason}" to ${rec.filterCategory}?`,
      confirmLabel: "Apply",
      onConfirm: async () => {
        setProcessingConfirmation(true);
        setProcessingIds((prev) => new Set(prev).add(id));
        try {
          await autoTuneApi.applyRecommendation(id);
          showNotification("Recommendation applied successfully", "success");
          loadPendingRecs();
          loadHistory();
          loadStatus();
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Failed to apply recommendation";
          showNotification(msg, "error");
          logger.error("[AutoTuneTab] handleApply:", err instanceof Error ? err : new Error(String(err)));
        } finally {
          setProcessingConfirmation(false);
          setProcessingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setConfirmation(null);
        }
      },
    });
  }, [pendingRecs, showNotification, loadPendingRecs, loadHistory, loadStatus]);

  const handleReject = useCallback(async (id: string) => {
    setProcessingIds((prev) => new Set(prev).add(id));
    try {
      await autoTuneApi.rejectRecommendation(id);
      showNotification("Recommendation rejected", "info");
      loadPendingRecs();
      loadHistory();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to reject recommendation";
      showNotification(msg, "error");
      logger.error("[AutoTuneTab] handleReject:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [showNotification, loadPendingRecs, loadHistory]);

  const handleRollback = useCallback(async (id: string) => {
    setConfirmation({
      title: "Rollback Recommendation",
      message: "Rollback this recommendation to its previous state?",
      confirmLabel: "Rollback",
      onConfirm: async () => {
        setProcessingConfirmation(true);
        setProcessingIds((prev) => new Set(prev).add(id));
        try {
          await autoTuneApi.rollbackRecommendation(id, "Manual rollback by admin");
          showNotification("Rollback performed successfully", "success");
          loadHistory();
          loadPendingRecs();
          loadStatus();
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Failed to rollback recommendation";
          showNotification(msg, "error");
          logger.error("[AutoTuneTab] handleRollback:", err instanceof Error ? err : new Error(String(err)));
        } finally {
          setProcessingConfirmation(false);
          setProcessingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setConfirmation(null);
        }
      },
    });
  }, [showNotification, loadHistory, loadPendingRecs, loadStatus]);

  const handleRunAnalysis = useCallback(async () => {
    setAnalysing(true);
    try {
      await autoTuneApi.runAnalysis();
      showNotification("Analysis triggered successfully", "success");
      loadStatus();
      loadPendingRecs();
      loadFilterStats();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to run analysis";
      showNotification(msg, "error");
      logger.error("[AutoTuneTab] handleRunAnalysis:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setAnalysing(false);
    }
  }, [showNotification, loadStatus, loadPendingRecs, loadFilterStats]);

  const [learningCycleRunning, setLearningCycleRunning] = useState(false);

  const handleRunLearningCycle = useCallback(async () => {
    setLearningCycleRunning(true);
    try {
      const result = await autoTuneApi.runLearningCycle();
      if (result.status === "success") {
        showNotification(
          `Learning cycle completed: ${result.processedTrades} trades, ${result.bridgeOptimizationSuggestions} optimizations`,
          "success"
        );
      } else {
        showNotification(
          `Learning cycle ${result.status}: ${result.error || "Unknown error"}`,
          "error"
        );
      }
      loadStatus();
      loadPendingRecs();
      loadFilterStats();
      loadHistory();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to run learning cycle";
      showNotification(msg, "error");
      logger.error("[AutoTuneTab] handleRunLearningCycle:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLearningCycleRunning(false);
    }
  }, [showNotification, loadStatus, loadPendingRecs, loadFilterStats, loadHistory]);

  const handleRunRejectedAnalysis = useCallback(async () => {
    setAnalysing(true);
    try {
      await autoTuneApi.runRejectedAnalysis();
      showNotification("Rejected analysis triggered successfully", "success");
      loadRejectedAnalysis();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to run rejected analysis";
      showNotification(msg, "error");
      logger.error("[AutoTuneTab] handleRunRejectedAnalysis:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setAnalysing(false);
    }
  }, [showNotification, loadRejectedAnalysis]);

  const handleSaveSettings = useCallback(async (newSettings: Record<string, unknown>) => {
    setSavingSettings(true);
    try {
      await autoTuneApi.updateSettings(newSettings);
      showNotification("Settings saved successfully", "success");
      loadSettings();
      loadStatus();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save settings";
      showNotification(msg, "error");
      logger.error("[AutoTuneTab] handleSaveSettings:", err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSavingSettings(false);
    }
  }, [showNotification, loadSettings, loadStatus]);

  const handleModeChange = useCallback(async (mode: string) => {
    try {
      await autoTuneApi.changeMode(mode);
      showNotification(`Mode changed to ${mode}`, "success");
      loadStatus();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to change mode";
      showNotification(msg, "error");
      logger.error("[AutoTuneTab] handleModeChange:", err instanceof Error ? err : new Error(String(err)));
    }
  }, [showNotification, loadStatus]);

  const dashboardCards = useMemo(() => {
    const pendingCount = pendingRecs.length;
    const appliedCount = historyItems.filter((h) => h.status === "applied").length;
    const filterEvalCount = filterStats.length;
    return { pendingCount, appliedCount, filterEvalCount };
  }, [pendingRecs, historyItems, filterStats]);

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {renderNotificationBanner(notification, () => setNotification(null))}

      {confirmation && renderConfirmationDialog(
        confirmation,
        () => setConfirmation(null),
        processingConfirmation
      )}

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#f1f5f9" }}>
              Auto-Tune
            </h2>
            <p style={styles.subtitle}>
              Automated parameter and filter optimization
            </p>
          </div>
        </div>
        <div style={styles.headerRight}>
          {statusLoading ? (
            <Skeleton height="28px" width="160px" />
          ) : statusError ? null : status ? (
            <>
              <div
                style={{
                  ...styles.statusBadge,
                  background: status.enabled
                    ? "rgba(34, 197, 94, 0.15)"
                    : "rgba(148, 163, 184, 0.15)",
                  color: status.enabled
                    ? "#22c55e"
                    : "#94a3b8",
                }}
              >
                <span style={{
                  width: "8px", height: "8px", borderRadius: "50%",
                  background: "currentColor", display: "inline-block",
                  animation: status.enabled ? "spin 1s linear infinite" : "none",
                }} />
                {status.enabled ? "RUNNING" : "DISABLED"}
              </div>
              <div style={{ ...styles.modeBadge, background: "rgba(139, 92, 246, 0.15)", color: "#a78bfa" }}>
                {status.mode}
              </div>
            </>
          ) : null}
          <Button
            variant="primary"
            size="md"
            onClick={handleRunAnalysis}
            disabled={analysing}
          >
            {analysing ? "Analysing..." : "Run Analysis"}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={handleRunLearningCycle}
            disabled={learningCycleRunning}
            style={{ marginLeft: "8px" }}
          >
            {learningCycleRunning ? "Running..." : "Run Full Learning Cycle"}
          </Button>
        </div>
      </div>

      <div style={styles.cardsRow}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Pending Recommendations</div>
          <div style={{ ...styles.statValue, color: "#fbbf24" }}>
            {statusLoading ? <Skeleton height="24px" width="40px" /> : dashboardCards.pendingCount}
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Applied Changes</div>
          <div style={{ ...styles.statValue, color: "#22c55e" }}>
            {statusLoading ? <Skeleton height="24px" width="40px" /> : dashboardCards.appliedCount}
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Filter Evaluations</div>
          <div style={{ ...styles.statValue, color: "#3b82f6" }}>
            {statusLoading ? <Skeleton height="24px" width="40px" /> : dashboardCards.filterEvalCount}
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Auto-Refresh (30s)</div>
          <div style={{ ...styles.statValue, color: "#94a3b8", fontSize: "0.9rem", fontWeight: 400 }}>
            Active
          </div>
        </div>
      </div>

      <div style={styles.subTabsNav}>
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              ...styles.subTabButton,
              ...(activeSubTab === tab.id ? styles.subTabButtonActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === "pending" && (
        <PendingRecommendationsList
          recommendations={pendingRecs}
          loading={pendingRecsLoading}
          error={pendingRecsError}
          processingIds={processingIds}
          onApply={handleApply}
          onReject={handleReject}
        />
      )}

      {activeSubTab === "filters" && (
        <FilterPerformanceGrid
          filterStats={filterStats}
          loading={filterStatsLoading}
          error={filterStatsError}
        />
      )}

      {activeSubTab === "rejected" && (
        <RejectedAnalysisPanel
          analysis={rejectedAnalysis}
          loading={rejectedAnalysisLoading}
          error={rejectedAnalysisError}
          onRunAnalysis={handleRunRejectedAnalysis}
          analysing={analysing}
        />
      )}

      {activeSubTab === "history" && (
        <HistoryTable
          items={historyItems}
          loading={historyLoading}
          error={historyError}
          processingIds={processingIds}
          onRollback={handleRollback}
        />
      )}

      {activeSubTab === "performance" && (
        <PerformanceAnalytics isVisible={activeSubTab === "performance"} />
      )}

      {activeSubTab === "settings" && (
        <SettingsPanel
          settings={settings}
          loading={settingsLoading}
          error={settingsError}
          saving={savingSettings}
          onSave={handleSaveSettings}
          onModeChange={handleModeChange}
        />
      )}

      {notification && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            padding: "12px 20px",
            borderRadius: "8px",
            background: notification.type === "error" ? "#ef4444" : notification.type === "info" ? "#3b82f6" : "#22c55e",
            color: "white",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            zIndex: 9999,
          }}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
}